// Fullscreen toggle. Ports the source-of-truth `toggleFullscreen` (see
// /var/www/kitchenos/portal-standalone.html:33128) into a self-contained
// chrome component that returns its button + a teardown for the
// `fullscreenchange` / `webkitfullscreenchange` listeners.

import { renderIcon } from "@tkos/shared-ui";

interface FullscreenAdapter {
  requestFullscreen(): Promise<void>;
  exitFullscreen(): Promise<void>;
  isFullscreen(): boolean;
}

function getAdapter(): FullscreenAdapter {
  const docEl = document.documentElement as HTMLElement & {
    webkitRequestFullscreen?: () => Promise<void> | void;
  };
  const doc = document as Document & {
    webkitFullscreenElement?: Element | null;
    webkitExitFullscreen?: () => Promise<void> | void;
  };

  return {
    requestFullscreen: async () => {
      if (docEl.requestFullscreen) await docEl.requestFullscreen().catch(() => {});
      else if (docEl.webkitRequestFullscreen) await docEl.webkitRequestFullscreen();
    },
    exitFullscreen: async () => {
      if (doc.exitFullscreen) await doc.exitFullscreen().catch(() => {});
      else if (doc.webkitExitFullscreen) await doc.webkitExitFullscreen();
    },
    isFullscreen: () => !!(doc.fullscreenElement || doc.webkitFullscreenElement),
  };
}

export interface FullscreenButton {
  el: HTMLButtonElement;
  destroy: () => void;
}

/**
 * Build a topbar fullscreen toggle button. Swaps its icon on
 * fullscreenchange events to mirror the source-of-truth behaviour.
 */
export function createFullscreenButton(): FullscreenButton {
  const fs = getAdapter();

  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "tkos-topbar__icon-btn tkos-topbar__fs-btn";
  btn.title = "Toggle fullscreen";
  btn.setAttribute("aria-label", "Toggle fullscreen");

  let iconEl: SVGElement = renderIcon("fullscreen", { size: "sm" });
  btn.appendChild(iconEl);

  const updateIcon = (): void => {
    const next = renderIcon(
      fs.isFullscreen() ? "fullscreen-exit" : "fullscreen",
      { size: "sm" },
    );
    iconEl.replaceWith(next);
    iconEl = next;
  };

  btn.addEventListener("click", () => {
    if (fs.isFullscreen()) {
      void fs.exitFullscreen();
    } else {
      void fs.requestFullscreen();
    }
  });

  document.addEventListener("fullscreenchange", updateIcon);
  document.addEventListener("webkitfullscreenchange", updateIcon);

  return {
    el: btn,
    destroy: () => {
      document.removeEventListener("fullscreenchange", updateIcon);
      document.removeEventListener("webkitfullscreenchange", updateIcon);
    },
  };
}
